import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject, tap } from 'rxjs';

import { filter } from 'rxjs/operators';

import { SlbLoadingService } from '@slb-dls/angular-material/loading';
import { SlbSeverity } from '@slb-dls/angular-material/notification';

import { ChartDataProvider } from '@ct-software/ct-ui-chart';
import { ConfirmModalService } from '@ct-software/ct-ui-core';
import { ACQ_TIME_CHANNEL } from './time-channel.const';
import { BackendApiService } from '../backendApi.service';
import { AppConfigService } from '../app-config.service';
import { ChannelConfig } from './channel-config.interface';

@Injectable({
  providedIn: 'root'
})
export class AcquisitionDataService implements ChartDataProvider {
  public channels: ChannelConfig[] = [];

  public readonly dataDomain$ = new BehaviorSubject<number[]>(null);
  public readonly forceUpdate$ = new Subject<void>();
  public initialData$: BehaviorSubject<object[]> = new BehaviorSubject<object[]>([]);
  public data$: Observable<object[]>;
  public lastUpdated: Date = new Date();

  private cachedResults: { [containerName: string]: string } = {};
  private readonly baseApiUrl: string = '/api';
  private readonly version: string = 'v1';

  constructor(
    private readonly confirmModalService: ConfirmModalService,
    private readonly loadingService: SlbLoadingService,
    private backendApiService: BackendApiService,
    private config: AppConfigService
  ) { }

  getDataForContainer(containerName: string): Promise<string> {
    if (this.cachedResults[containerName]) {
    return Promise.resolve(this.cachedResults[containerName]);
    }

    return this.backendApiService.getText(this.getUrl('GetJobData') + containerName).toPromise().then(x => {
      if (x.length != 0) {
        return this.cacheResult(x, containerName);
      }
      return this.backendApiService.getText(this.getUrl('GetAcquisitionData') + containerName).toPromise().then(x => {
        return this.cacheResult(x, containerName);
      })
    });
  }

  private getUrl(route: string): string {
    return `${this.baseApiUrl}/${route}?storageAccountEndpoint=${this.config.storageAccountUrl}/&containerName=`;
  }

  private getFullUrl(resourceUrl: string): string {
    return `${this.baseApiUrl}/${this.version}/${resourceUrl}`;
  }

  private cacheResult(result: string, containerName: string): string {
    this.cachedResults[containerName] = result;
    return result;
  }

  public reset(): void {
    this.channels = [];
    this.resetInitialData();
    this.dataDomain$.next(null);
  }

  public resetInitialData(): void {
    this.initialData$.next(null);
  }

  public forceUpdateChart(): void {
    this.forceUpdate$.next();
  }

  public getData$(start: number, end: number, step: number): Observable<object[]> {
    return this.data$;
  }

  public getExportWarningDialog$(): Observable<void> {
    return this.confirmModalService.open(
      {
        title: 'Warning',
        severity: SlbSeverity.Warning,
        confirmButtonText: 'Proceed',
        cancelButtonText: 'Cancel',
        closable: false,
        description: `Exporting this much data may lead to performance issues or crashes.
         Consider removing some channels or zooming in on a smaller interval of time`
      }
    ).afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => this.loadingService.showSpinner({ text: 'Working...' }))
      ) as Observable<void>;
  }

  public getDataForExport$(start: number, end: number): Observable<object[]> {
    return this.data$;
  }

  public shouldShowExportWarning(start: number, end: number): boolean {
    const twoHoursInMillis = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    const timeDifference = end - start;

    return timeDifference > twoHoursInMillis || this.channels.length > 100;
  }

  public getPointCount(start: number, end: number): number {
    return (end - start) / 1000;
  }

  public getDataDefaultDomain(): number[] {
    return this.dataDomain$.value;
  }

  public setInitialData(data: any): void {
    this.dataDomain$.next(this.getMinMax(data));
    this.initialData$.next(data);
  }

  private getMinMax(data: object[]): number[] {
    if (!data?.length) {
      return [];
    }

    const min = this.getDataMinAcqTime(data);
    const max = this.getDataMaxAcqTime(data);

    return [min, max];
  }

  private getDataMinAcqTime(data: object[]): number {
    const acqTime = data.find(i => i[ACQ_TIME_CHANNEL]);

    if (!acqTime) {
      return;
    }

    return acqTime[ACQ_TIME_CHANNEL].getTime();
  }

  private getDataMaxAcqTime(data: object[]): number {
    const acqTime = [...data].reverse().find(i => i[ACQ_TIME_CHANNEL]);

    if (!acqTime) {
      return;
    }

    return acqTime[ACQ_TIME_CHANNEL].getTime();
  }
}
